// @flow
/** @jsx isolateComponent */

import { useCallback } from 'react';

import Text from '@zapier/common-components/src/components/Text';
import { Colors, Duration } from '@zapier/theme';
import { isolateComponent, pluralize } from '@zapier/common-utils';

type Props = {
  /** The element IDs of DOM elements which label this input. */
  ariaLabelledBy?: string,
  /** The ID to give this DOM element (so it can be e.g. referenced by a label). */
  id?: string,
  /** Maximum range value */
  max: number,
  /** Minimum range value */
  min: number,
  /** Callback to be executed when the input's value is changed. */
  onChange: (value: number) => void,
  /** Show the value below the slider */
  showValue: boolean,
  /** Step interval of slider */
  step: number,
  /** Current value of slider */
  value: number,
  /** Label to show for value (used with showValue) */
  valueLabel?: string,
};

const thumbSize = 18;
const trackHeight = 5;

const shadow = {
  boxShadow: `0 0 0 2px ${Colors.white},
  0 0 0 4px ${Colors.captainAmerica}`,
};

const thumbStyle = {
  background: Colors.heroine,
  border: 0,
  borderRadius: 12,
  cursor: 'ew-resize',
  height: thumbSize,
  marginTop: 0 - thumbSize / 2 + trackHeight / 2, // subtrack borders if they exist
  width: thumbSize,
  transition: `box-shadow ${Duration.short} ease-in-out`,
};

const trackStyle = {
  background: Colors.adamantium,
  border: 0,
  borderRadius: 12,
  cursor: 'pointer',
  height: trackHeight,
};

const rangeSliderCss = {
  flex: 1,
  height: 22,
  outline: 'none',
  width: '100%',
  WebkitAppearance: 'none',

  '&:focus': {
    outline: 'none',

    '&::-webkit-slider-thumb': shadow,
    '&::-moz-range-thumb': shadow,
  },

  '&::-webkit-slider-runnable-track': {
    ...trackStyle,
    WebkitAppearance: 'none',
  },

  '&::-webkit-slider-thumb': {
    ...thumbStyle,
    WebkitAppearance: 'none',
  },

  '&::-moz-range-track': trackStyle,

  '&::-moz-range-thumb': thumbStyle,

  '&::-moz-focus-inner, &::-moz-focus-outer': {
    border: 0,
  },
};

const labelCss = {
  fontVariantNumeric: 'tabular-nums',
  marginLeft: 10,
  minWidth: 25,
  textAlign: 'center',
};

/**
 * Input Range Slider
 */
const RangeSlider = (props: Props) => {
  const { onChange } = props;

  const handleChange = useCallback(
    (e: SyntheticInputEvent<HTMLElement>) =>
      onChange ? onChange(Number(e.target.value)) : null,
    [onChange]
  );

  return (
    <div>
      <input
        aria-labelledby={props.ariaLabelledBy}
        aria-valuemax={props.max}
        aria-valuemin={props.min}
        aria-valuenow={props.value}
        css={rangeSliderCss}
        id={props.id}
        max={props.max}
        min={props.min}
        onChange={handleChange}
        step={props.step}
        type="range"
        value={props.value}
      />
      {props.showValue && (
        <output css={labelCss} htmlFor={props.id}>
          <Text type="body">
            {props.value}
            {props.valueLabel
              ? ` ${pluralize(props.valueLabel, props.value)}`
              : ''}
          </Text>
        </output>
      )}
    </div>
  );
};

RangeSlider.defaultProps = {
  max: 100,
  min: 0,
  showValue: false,
  step: 1,
};

export default RangeSlider;
